import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import HRHeader from "../components/HRHeader";
import api, { uploadFile, openFile } from "../api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, Clock, Hourglass, Plus, X, CalendarIcon, FileText, Upload, Paperclip } from "lucide-react";
import InteractiveTour from "../components/InteractiveTour";
import { GuidaRapidaButton } from "../components/GuidaRapidaButton";
import { usePageTour } from "../hooks/usePageTour";
import { ferieTour } from "../miniTours";

export const TYPE_LABELS = {
  ferie: "Ferie",
  permesso: "Permesso orario",
  malattia: "Malattia",
  permesso_104: "Permesso Legge 104",
  lutto: "Congedo per lutto",
  congedo_maternita: "Congedo maternità/paternità",
  congedo_parentale: "Congedo parentale",
  ex_festivita: "Ex festività",
  permesso_studio: "Permesso studio (150 ore)",
  aspettativa: "Aspettativa non retribuita",
  preavviso: "Preavviso",
};

export const STATUS = {
  in_attesa: { label: "In attesa", cls: "bg-amber-100 text-amber-700" },
  approvata: { label: "Approvata", cls: "bg-green-100 text-green-700" },
  rifiutata: { label: "Rifiutata", cls: "bg-red-100 text-red-700" },
  annullata: { label: "Annullata", cls: "bg-gray-100 text-gray-500" },
  revocata: { label: "Revocata", cls: "bg-orange-100 text-orange-700" },
};

const fmt = (iso) => {
  try { return format(new Date(iso), "dd MMM yyyy", { locale: it }); } catch { return iso; }
};

const DatePicker = ({ value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="w-full flex items-center gap-2 border border-gray-300 rounded-sm px-3 py-2.5 text-[14px] text-left hover:border-[#16345c] transition-colors">
          <CalendarIcon size={16} className="text-gray-400" />
          {value ? format(value, "dd/MM/yyyy") : <span className="text-gray-400">{placeholder}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={(d) => { onChange(d); setOpen(false); }} locale={it} />
      </PopoverContent>
    </Popover>
  );
};

const FeriePage = () => {
  const { user } = useAuth();
  const canPreavviso = user?.role === "admin" || user?.role === "responsabile";
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState([]);
  const [type, setType] = useState("ferie");
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [hours, setHours] = useState("");
  const [halfDay, setHalfDay] = useState(false);
  const [reason, setReason] = useState("");
  const [certificate, setCertificate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const tour = usePageTour("ferie");

  const load = () => {
    api.get("/leaves/mine").then((r) => setList(r.data)).catch(() => {});
    api.get("/auth/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const HOURS_TYPES = ["permesso", "ex_festivita", "permesso_studio"];
  const isHours = HOURS_TYPES.includes(type);
  const isMalattia = type === "malattia";
  const hoursMax = type === "permesso_studio" ? 150 : type === "ex_festivita" ? 40 : 8;
  const balanceHint = !summary ? null
    : type === "permesso" ? `Saldo ROL: ${summary.rol_balance} ore`
    : type === "ex_festivita" ? `Saldo ex festività: ${summary.exfestivita_balance ?? 0} ore`
    : type === "permesso_studio" ? `Saldo permessi studio: ${summary.studio_balance ?? 0} ore (diritto annuo 150)`
    : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!start) { toast.error("Seleziona la data di inizio"); return; }
    if (!isHours && !end) { toast.error("Seleziona la data di fine"); return; }
    if (isHours && (!hours || Number(hours) <= 0)) { toast.error("Indica le ore di permesso"); return; }
    setSubmitting(true);
    try {
      let certificate_file_id = null;
      if (isMalattia && certificate) {
        const up = await uploadFile(certificate);
        certificate_file_id = up.id;
      }
      const payload = {
        type,
        start_date: format(start, "yyyy-MM-dd"),
        end_date: format(isHours ? start : end, "yyyy-MM-dd"),
        half_day: halfDay,
        hours: isHours ? Number(hours) : null,
        reason,
        certificate_file_id,
      };
      await api.post("/leaves", payload);
      toast.success("Richiesta inviata! In attesa di approvazione.");
      setStart(null); setEnd(null); setHours(""); setReason(""); setHalfDay(false); setCertificate(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id) => {
    try {
      await api.put(`/leaves/${id}/cancel`);
      toast.success("Richiesta annullata");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const cards = [
    { l: "Ferie residue", v: summary ? summary.ferie_balance : "-", u: "giorni", icon: CalendarDays, c: "#16345c" },
    { l: "Permessi ROL", v: summary ? summary.rol_balance : "-", u: "ore", icon: Clock, c: "#009640" },
    { l: "Ex festività", v: summary ? (summary.exfestivita_balance ?? 0) : "-", u: "ore", icon: CalendarDays, c: "#0d6cb8" },
    { l: "Permesso studio", v: summary ? (summary.studio_balance ?? 0) : "-", u: "ore", icon: Clock, c: "#8e44ad" },
    { l: "In attesa", v: summary ? summary.leaves_pending : "-", u: "richieste", icon: Hourglass, c: "#e67e22" },
  ];

  return (
    <div className="min-h-screen bg-[#f0f2f5]">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-[26px] font-black text-[#16345c] mb-1">Ferie e Permessi</h1>
            <p className="text-[14px] text-gray-500">Richiedi assenze e monitora lo stato di approvazione</p>
          </div>
          <GuidaRapidaButton onClick={tour.open} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8" data-testid="ferie-saldi">
          {cards.map((c) => (
            <div key={c.l} className="bg-white border border-gray-200 rounded-lg p-5 flex items-center gap-4">
              <span className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${c.c}1a`, color: c.c }}><c.icon size={24} /></span>
              <div>
                <div className="text-[26px] font-black text-[#16345c] leading-none">{c.v}</div>
                <div className="text-[12px] text-gray-500">{c.u} · {c.l}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-32">
              <h2 className="text-[16px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><Plus size={18} /> Nuova richiesta</h2>
              <form onSubmit={submit} className="space-y-4">
                <div data-testid="ferie-field-type">
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Tipo di assenza</label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_LABELS).filter(([k]) => k !== "preavviso" || canPreavviso).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div data-testid="ferie-field-dates">
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">{isHours ? "Giorno" : "Dal"}</label>
                  <DatePicker value={start} onChange={setStart} placeholder="Seleziona data" />
                </div>

                {!isHours && (
                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Al</label>
                    <DatePicker value={end} onChange={setEnd} placeholder="Seleziona data" />
                  </div>
                )}

                {isHours ? (
                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Ore</label>
                    <input type="number" min="1" max={hoursMax} step="0.5" value={hours} onChange={(e) => setHours(e.target.value)}
                      placeholder="es. 4" className="w-full border border-gray-300 rounded-sm px-3 py-2.5 text-[14px] outline-none focus:border-[#16345c]" />
                    {balanceHint && <p className="text-[11px] text-gray-500 mt-1">{balanceHint}</p>}
                  </div>
                ) : (
                  <label className="flex items-center gap-2 text-[13px] text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={halfDay} onChange={(e) => setHalfDay(e.target.checked)} className="accent-[#16345c]" />
                    Mezza giornata
                  </label>
                )}

                {isMalattia && (
                  <div>
                    <label className="block text-[12px] font-semibold text-gray-600 mb-1">Certificato medico (PDF)</label>
                    <label className="flex items-center gap-2 border border-dashed border-gray-300 rounded-sm px-3 py-2.5 text-[13px] cursor-pointer hover:border-[#16345c] transition-colors">
                      <Upload size={16} className="text-gray-400 shrink-0" />
                      <span className={`truncate ${certificate ? "text-[#16345c] font-semibold" : "text-gray-400"}`}>
                        {certificate ? certificate.name : "Carica certificato (PDF o immagine)"}
                      </span>
                      <input type="file" accept="application/pdf,image/*" className="hidden"
                        onChange={(e) => setCertificate(e.target.files?.[0] || null)} />
                    </label>
                  </div>
                )}

                <div data-testid="ferie-field-note">
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Note (facoltative)</label>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                    placeholder="Motivo della richiesta..." className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c] resize-none" />
                </div>

                <button type="submit" disabled={submitting} data-testid="ferie-submit"
                  className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold text-[14px] py-3 rounded-sm uppercase tracking-wide transition-colors disabled:opacity-60">
                  {submitting ? "Invio..." : "Invia richiesta"}
                </button>
              </form>
            </div>
          </div>

          {/* List */}
          <div className="lg:col-span-2" data-testid="ferie-list">
            <h2 className="text-[16px] font-bold text-[#16345c] mb-4">Le mie richieste</h2>
            {list.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
                <FileText size={40} className="mx-auto mb-3 opacity-40" />
                Nessuna richiesta presente
              </div>
            ) : (
              <div className="space-y-3">
                {list.map((lv) => (
                  <div key={lv.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[14px] font-bold text-[#16345c]">{TYPE_LABELS[lv.type] || lv.type}</span>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS[lv.status]?.cls}`}>{STATUS[lv.status]?.label}</span>
                      </div>
                      <div className="text-[13px] text-gray-600">
                        {lv.type === "permesso"
                          ? `${fmt(lv.start_date)} \u00b7 ${lv.hours} ore`
                          : `${fmt(lv.start_date)} \u2192 ${fmt(lv.end_date)} \u00b7 ${lv.days_count} ${lv.days_count === 1 ? "giorno" : "giorni"}`}
                      </div>
                      {lv.reason && <div className="text-[12.5px] text-gray-400 mt-1 italic">"{lv.reason}"</div>}
                      {lv.type === "malattia" && lv.certificate_file_id && (
                        <button onClick={() => openFile(lv.certificate_file_id)} className="flex items-center gap-1 text-[12px] font-semibold text-[#0d6cb8] hover:underline mt-1.5">
                          <Paperclip size={13} /> Scarica certificato medico
                        </button>
                      )}
                      {lv.decision_note && (
                        <div className="text-[12px] mt-2 text-gray-500">
                          <b>{lv.approver_name}:</b> {lv.decision_note}
                        </div>
                      )}
                    </div>
                    {lv.status === "in_attesa" && (
                      <button onClick={() => cancel(lv.id)} className="shrink-0 flex items-center gap-1 text-[12px] font-semibold text-[#c00510] hover:bg-red-50 px-2 py-1 rounded-sm transition-colors">
                        <X size={14} /> Annulla
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      {tour.show && <InteractiveTour steps={ferieTour} requireAck={false} onClose={tour.close} />}
    </div>
  );
};

export default FeriePage;
